/**
 * How the document viewer toolbar folds, and what it folds into.
 *
 * The toolbar folds in named tiers, widest first. Which tier is in force is
 * not a breakpoint: `DocumentViewerToolbar` measures how wide each tier's
 * content would be and takes the widest that fits the room it has. A document
 * with a short type name and no version dropdown therefore keeps its labels
 * far longer than one with both — a fixed width could not do that, because it
 * had to be cut for the widest content there is, and folded every other
 * document early while the bar still had room to spare.
 *
 * The chosen tier is written onto the toolbar row as cumulative data
 * attributes (`data-tier-medium`, then `-labels`, then `-full`), and the class
 * strings below read them through the row's `group/toolbar`. Every value is a
 * complete class string on purpose: Tailwind scans source text for
 * candidates, so a class assembled from parts would never reach the
 * stylesheet.
 */
export const TOOLBAR_TIERS = ["full", "labels", "medium", "compact"] as const;
export type ToolbarTier = (typeof TOOLBAR_TIERS)[number];

export const toolbarTier = {
  /** Goes on the toolbar row; every variant below keys off it. */
  group: "group/toolbar",

  /** The names on the buttons. */
  fromLabelsInline: "hidden group-data-[tier-labels]/toolbar:inline",

  /**
   * The widest controls — the search field, the zoom steppers, export as two
   * buttons. The search field alone costs ~150px more than its icon.
   */
  fromFullFlex: "hidden group-data-[tier-full]/toolbar:flex",
  fromFullInlineFlex: "hidden group-data-[tier-full]/toolbar:inline-flex",
  /** The folded stand-in for a group that only unfolds at full width. */
  belowFull: "group-data-[tier-full]/toolbar:hidden",

  /** Groups that survive the medium tier, and their folded stand-ins. */
  fromMediumFlex: "hidden group-data-[tier-medium]/toolbar:flex",
  fromMediumInlineFlex: "hidden group-data-[tier-medium]/toolbar:inline-flex",
  belowMedium: "group-data-[tier-medium]/toolbar:hidden",

  /**
   * Compact is where nothing fits on one line even folded, so the bar wraps
   * to a second row rather than squeezing the version date — the one thing on
   * it a reader cannot infer from anywhere else on screen. The spacers go with
   * it, so the groups pack from the left instead of stranding one on a line of
   * its own.
   */
  barWrap: "flex-wrap group-data-[tier-medium]/toolbar:flex-nowrap",
  spacer: "hidden flex-1 group-data-[tier-medium]/toolbar:block",

  /**
   * Caps the end of a segment whose neighbour has folded away. `:first-child`
   * and `:last-child` follow the DOM rather than visibility, so the group's
   * own rounding lands on the hidden neighbour and would leave this one square
   * in mid-air. The caps carry `!` because the group sets its rounding from the
   * parent, which outranks a plain utility on the child — and so the tier that
   * brings the neighbour back has to take them off with `!` as well.
   */
  capLeftBelowFull:
    "rounded-l-4xl! border-l! group-data-[tier-full]/toolbar:rounded-l-none! group-data-[tier-full]/toolbar:border-l-0!",
  capRightBelowFull:
    "rounded-r-4xl! group-data-[tier-full]/toolbar:rounded-r-none!",
  capBothWhenCompact:
    "rounded-l-4xl! border-l! rounded-r-4xl! group-data-[tier-medium]/toolbar:rounded-l-none! group-data-[tier-medium]/toolbar:border-l-0! group-data-[tier-medium]/toolbar:rounded-r-none!",
} as const;
